import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import {
  ApproveRejectActions,
  ControlledFileUpload,
  ControlledTextField,
  ErrorSummary,
  SectionTitle,
} from 'modules/shared/components';
import { ensureFileId } from 'modules/shared/service';
import { formatDateTime } from 'modules/shared/utils';
import { DOCUMENT_SLOTS } from '../../shared/constants';
import { InlineRejectSection } from '../components';

const CURRENT_ROUND_KEY = 'documentCollection';

export default function DocumentCollectionRound({ interviewData, readOnly, onApprove, onReject }) {
  const [isAdding, setIsAdding] = useState(false);
  const [actionMode, setActionMode] = useState(null);

  const backendDocuments = useMemo(
    () => interviewData?.personalData?.documents || [],
    [interviewData],
  );

  const orderedDocuments = useMemo(() => {
    const backendMap = new Map(backendDocuments.map((d) => [d.name, d]));

    const predefined = DOCUMENT_SLOTS.map((slot) => ({
      name: slot.name,
      required: slot.required,
      backendFile: backendMap.get(slot.name),
    }));

    const additional = backendDocuments
      .filter((d) => !DOCUMENT_SLOTS.some((s) => s.name === d.name))
      .map((d) => ({
        name: d.name,
        required: false,
        backendFile: d,
      }));

    return [...predefined, ...additional];
  }, [backendDocuments]);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: { documents: {}, newDoc: {}, rejectionReason: '' },
  });

  const { watch, setValue, clearErrors, trigger, resetField } = methods;
  const documents = watch('documents') || {};
  const rejectionReason = watch('rejectionReason');

  const existingNames = useMemo(
    () => new Set(orderedDocuments.map((d) => d.name)),
    [orderedDocuments],
  );

  const resetAction = () => {
    setActionMode(null);
    resetField('rejectionReason');
  };

  const handleApprove = async () => {
    const fileEntries = Object.entries(documents);

    const uploadedDocuments = await Promise.all(
      fileEntries.map(async ([name, value]) => {
        const fileId = await ensureFileId(value);
        if (!fileId) return null;

        return {
          name,
          id: fileId,
          uploadedBy: 'hr',
          uploadedDate: new Date().toISOString(),
        };
      }),
    );

    onApprove?.({
      action: 'submit',
      personalData: { documents: uploadedDocuments.filter(Boolean) },
      induction: { backgroundVerification: true },
      currentRoundKey: CURRENT_ROUND_KEY,
    });
  };

  const handleReject = async () => {
    const isValid = await trigger('rejectionReason', { shouldFocus: true });
    if (!isValid) return;

    onReject?.({
      action: 'reject',
      rejectionReason,
      induction: { backgroundVerification: false },
    });
  };

  return (
    <FormProvider {...methods}>
      <form className='column gap-3'>
        <SectionTitle
          title='DOCUMENT COLLECTION'
          action={
            !readOnly && (
              <button
                type='button'
                className='btn text-btn'
                onClick={() => setIsAdding(true)}
                disabled={readOnly}
              >
                + Add Document
              </button>
            )
          }
        />

        <ErrorSummary />

        <div className='column'>
          {orderedDocuments.map((doc) => (
            <DocumentRow
              key={doc.name}
              name={doc.name}
              required={doc.required}
              backendFile={doc.backendFile}
              pendingFile={documents[doc.name]}
              readOnly={readOnly}
            />
          ))}

          {!readOnly && isAdding && (
            <AddDocumentInlineRow
              existingNames={existingNames}
              onAdd={(name, file) => {
                setValue(`documents.${name}` as any, { file });
                setValue('newDoc', {});
                clearErrors('newDoc');
                setIsAdding(false);
              }}
              onCancel={() => {
                setValue('newDoc', {});
                clearErrors('newDoc');
                setIsAdding(false);
              }}
            />
          )}

          <div className='row row--between'>
            <span>Background Verification</span>
            {!readOnly && (
              <ApproveRejectActions
                onApprove={handleApprove}
                onReject={() => setActionMode('reject')}
                approveDisabled={!!actionMode}
                rejectDisabled={!!actionMode}
              />
            )}

            {readOnly && (
              <span>
                {interviewData?.induction?.backgroundVerification ? 'Approved' : 'Rejected'}
              </span>
            )}
          </div>
          {actionMode === 'reject' && (
            <InlineRejectSection
              name='rejectionReason'
              onCancel={resetAction}
              onConfirm={handleReject}
              disabled={readOnly}
            />
          )}
        </div>
      </form>
    </FormProvider>
  );
}

function DocumentRow({ name, required, backendFile, pendingFile, readOnly }) {
  const previewUrl = useMemo(() => {
    if (backendFile?.url) return backendFile.url;

    const file =
      pendingFile instanceof File
        ? pendingFile
        : pendingFile?.file instanceof File
          ? pendingFile.file
          : null;

    return file ? URL.createObjectURL(file) : null;
  }, [backendFile, pendingFile]);

  useEffect(() => {
    return () => {
      if (pendingFile instanceof File && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [pendingFile, previewUrl]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <span>
        {name}
        {required && <span className='required'>*</span>}
      </span>

      <span className='text--secondary'>
        {backendFile
          ? `Uploaded by ${backendFile.uploadedBy} · ${formatDateTime(backendFile.uploadedDate)}`
          : pendingFile
            ? 'File selected (pending upload)'
            : 'Not uploaded'}
      </span>

      <div className='row gap-3 justify-self-end'>
        {previewUrl && (
          <MuiLink
            href={previewUrl}
            target='_blank'
            rel='noreferrer'
            sx={{ textDecoration: 'underline' }}
          >
            view
          </MuiLink>
        )}

        {!readOnly && <ControlledFileUpload name={`documents.${name}`} autoUpload={false} />}
      </div>
    </Box>
  );
}

function AddDocumentInlineRow({ existingNames, onAdd, onCancel }) {
  const { watch, trigger } = useFormContext();

  const name = watch('newDoc.name');
  const file = watch('newDoc.file');

  const handleAdd = async () => {
    const valid = await trigger('newDoc', { shouldFocus: true });
    if (!valid) return;
    onAdd(name.trim(), file);
  };

  return (
    <Box className='row row--between gap-3'>
      <ControlledTextField
        id='new-doc-name'
        name='newDoc.name'
        placeholder='Document name'
        rules={{
          required: 'Document name is required',
          validate: (v) =>
            !existingNames.has(v?.trim()) || 'A document with this name already exists',
        }}
        fullWidth
        sx={{ maxWidth: '50%' }}
      />

      <div className='row gap-3'>
        <ControlledFileUpload name='newDoc.file' autoUpload={false} />

        <button type='button' className='btn cta-btn' disabled={!name || !file} onClick={handleAdd}>
          Add
        </button>

        <button type='button' className='btn outlined-btn' onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Box>
  );
}
